import React from 'react';
import { FieldArrayWithId, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';

import InputField from '@/components/atoms/InputField';
import defaultUserAvatar from '@/assets/images/default-user.png';
import { getCurrencySymbol } from '@/utils/budgetCalculations';
import { formatAccountingCenterMemberType } from '@/utils/accountingCenterMembers';
import {
  AccountingCenterFormValues,
  AccountingCenterMemberFormValues
} from '@/services/types/accounting-centers-types';

interface ACMembersStepProps {
  register: UseFormRegister<AccountingCenterFormValues>;
  watch: UseFormWatch<AccountingCenterFormValues>;
  errors: FieldErrors<AccountingCenterFormValues>;
  fields: FieldArrayWithId<AccountingCenterFormValues, 'members', 'id'>[];
  currentUserEmail?: string;
  currentUserPhotoUrl?: string;
  currency?: string;
}

const ACMembersStep: React.FC<ACMembersStepProps> = ({
  register,
  watch,
  errors,
  fields,
  currentUserEmail,
  currentUserPhotoUrl,
  currency = 'EUR'
}) => {
  const currencySymbol = getCurrencySymbol(currency);

  const getMemberAvatarSrc = (member: AccountingCenterMemberFormValues | undefined, isCurrentUser: boolean) => {
    if (member?.profilePicUrl) {
      return member.profilePicUrl;
    }

    if (isCurrentUser && currentUserPhotoUrl) {
      return currentUserPhotoUrl;
    }

    return defaultUserAvatar;
  };

  const getMemberError = (index: number, field: keyof AccountingCenterMemberFormValues) => {
    const membersErrors = errors.members as FieldErrors<AccountingCenterMemberFormValues>[] | undefined;
    return membersErrors?.[index]?.[field];
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-bold">Team Members</h4>

      <div className="space-y-[22px] md:space-y-6">
        {fields.map((field, index) => {
          const member = watch(`members.${index}`);
          const isCurrentUser = !!currentUserEmail && member?.email?.toLowerCase() === currentUserEmail.toLowerCase();
          const maxOreHours = member?.maxOreHours;

          return (
            <div key={field.id} className="space-y-2">
              <p className="text-xs font-medium text-gray">
                {member?.email}
                {isCurrentUser && <span className="text-secondary"> (You)</span>}
              </p>

              <div className="grid grid-cols-[40px_1fr] gap-2 md:flex md:items-start md:gap-4">
                <img
                  src={getMemberAvatarSrc(member, isCurrentUser)}
                  alt={member?.memberName || 'Member'}
                  className="col-start-1 row-start-1 size-10 rounded-full object-cover bg-lightGray shrink-0 md:col-auto md:row-auto"
                  onError={(event) => {
                    event.currentTarget.src = defaultUserAvatar;
                  }}
                />

                <div className="col-start-2 row-start-1 min-w-0 md:flex-1 md:col-auto md:row-auto">
                  <p className="text-sm font-medium truncate">{member?.memberName}</p>
                  <p className="text-secondary text-xs truncate">
                    {formatAccountingCenterMemberType(member?.type ?? '')}
                  </p>
                </div>

                <div className="col-span-2 row-start-2 grid grid-cols-2 gap-2 md:contents">
                  <div className="min-w-0 md:flex-1">
                    <InputField
                      name={`members.${index}.oreHours`}
                      placeholder="ore hours..."
                      type="number"
                      register={register}
                      error={getMemberError(index, 'oreHours')}
                      size="sm"
                      className="w-full"
                      unitDescription="Hours"
                      min={0}
                    />
                    {maxOreHours != null && !getMemberError(index, 'oreHours') && (
                      <p className="text-secondary text-xs mt-[0.35rem]">cannot exceed {maxOreHours} Hours</p>
                    )}
                  </div>

                  <InputField
                    name={`members.${index}.hourlyRate`}
                    placeholder="rate..."
                    type="number"
                    register={register}
                    size="sm"
                    className="min-w-0 md:flex-1"
                    inputClassName="disabled:placeholder:text-[#ADB5BD] disabled:opacity-80"
                    unitDescription={
                      <p>
                        <span className="font-bold">{currencySymbol}</span> /Hours
                      </p>
                    }
                    disabled
                    min={0}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ACMembersStep;
